import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, AlertTriangle, DollarSign, ImageOff, FileText, Clock, ExternalLink } from 'lucide-react';
import { buildProductUrl } from '../../../utils/ikea-urls';
import { api } from '../../../services/api';

type ProductIssue = 'missing_price' | 'missing_image' | 'missing_name' | 'stale';

interface AsIsProductsTableProps {
  storeId: string;
  country: string;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function IssueBadge({ issue }: { issue: ProductIssue }) {
  const config = {
    missing_price: { icon: DollarSign, label: 'Price', color: 'bg-amber-100 text-amber-700' },
    missing_image: { icon: ImageOff, label: 'Image', color: 'bg-amber-100 text-amber-700' },
    missing_name: { icon: FileText, label: 'Name', color: 'bg-amber-100 text-amber-700' },
    stale: { icon: Clock, label: 'Stale', color: 'bg-red-100 text-red-700' },
  };

  const { icon: Icon, label, color } = config[issue];

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded ${color}`}>
      <Icon size={10} />
      {label}
    </span>
  );
}

export function AsIsProductsTable({ storeId, country }: AsIsProductsTableProps) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'issues'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-store-asis-products', storeId, page, search, filter],
    queryFn: () =>
      api.getAdminStoreAsIsProducts(storeId, {
        page,
        limit: 50,
        search: search || undefined,
        filter,
      }),
  });

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load products</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by product code or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              setFilter('all');
              setPage(1);
            }}
            className={`px-3 py-2 text-sm rounded-lg ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setFilter('issues');
              setPage(1);
            }}
            className={`px-3 py-2 text-sm rounded-lg flex items-center gap-1.5 ${
              filter === 'issues'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <AlertTriangle size={14} />
            Issues Only
          </button>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {data.products.length} of {data.total} products
          </span>
          <div className="flex gap-4">
            <span>Missing Price: {data.summary.missing_price}</span>
            <span>Missing Image: {data.summary.missing_image}</span>
            <span>Stale: {data.summary.stale_products}</span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Checked</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : data?.products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                data?.products.map((product) => (
                  <tr key={product.product_code} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name || ''}
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                            <ImageOff size={16} className="text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {product.name || 'Unknown'}
                          </p>
                          <a href={buildProductUrl(product.product_code, country)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:text-primary-700 hover:underline flex items-center gap-1">{product.product_code} <ExternalLink size={10} /></a>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {product.current_price !== null ? (
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            ${product.current_price.toFixed(2)}
                          </span>
                          {product.original_price !== null && product.original_price !== product.current_price && (
                            <span className="text-xs text-gray-500 line-through ml-2">
                              ${product.original_price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {product.is_as_is && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                            As-Is
                          </span>
                        )}
                        {product.is_clearance && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                            Clearance
                          </span>
                        )}
                        {product.availability && (
                          <span className="text-xs text-gray-500">{product.availability}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {formatTimeAgo(product.last_checked)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {product.issues.length === 0 ? (
                          <span className="text-xs text-green-600">✓ Complete</span>
                        ) : (
                          product.issues.map((issue) => (
                            <IssueBadge key={issue} issue={issue} />
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Page {page} of {data.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
